export interface UrlIdEntry {
  url: string;
  id: string;
}

export const ADMIN_ONLY_PREFERENCE_PATHS = [
  '/home/audit-log',
  '/home/billing',
  '/home/integrations',
  '/home/business-accounts',
  '/home/social-accounts',
  '/home/team-management',
  '/home/short-links',
];

export function isAdminPreferencePath(pathname: string): boolean {
  return ADMIN_ONLY_PREFERENCE_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

type Group = { id: string; secondaryItems?: Array<{ url?: string }> };
type Standalone = { url?: string };

export function standaloneId(url: string): string {
  if (url === '/home') return 'overview';
  if (url === '/home/inbox') return 'inbox';
  return url;
}

export function pathMatchesUrl(pathname: string, url: string): boolean {
  const base = url.split('?')[0];
  if (base === '/home') {
    return pathname === '/home' || pathname === '/home/' || pathname === '/home/agent';
  }
  return pathname === base || pathname.startsWith(base + '/');
}

export const LEGACY_GOVERNED_PATHS: UrlIdEntry[] = [
  { url: '/home/whatsapp-ai-agent', id: 'ai-tools' },
  { url: '/home/live-chat', id: 'ai-tools' },
  { url: '/home/voice-agents', id: 'ai-tools' },
  { url: '/home/chatbot', id: 'ai-tools' },
];

export function buildUrlIdEntries(standaloneItems: Standalone[], groups: Group[]): UrlIdEntry[] {
  const entries: UrlIdEntry[] = [];
  for (const item of standaloneItems || []) {
    if (item?.url) entries.push({ url: item.url, id: standaloneId(item.url) });
  }
  for (const group of groups || []) {
    for (const sub of group.secondaryItems || []) {
      if (sub?.url) entries.push({ url: sub.url, id: group.id });
    }
  }
  for (const legacy of LEGACY_GOVERNED_PATHS) {
    if (!entries.some((e) => e.url.split('?')[0] === legacy.url)) {
      entries.push(legacy);
    }
  }
  return entries;
}

export function resolveIdFromEntries(pathname: string, entries: UrlIdEntry[]): string | null {
  let best: UrlIdEntry | null = null;
  for (const entry of entries) {
    if (pathMatchesUrl(pathname, entry.url)) {
      if (!best || entry.url.length > best.url.length) best = entry;
    }
  }
  return best ? best.id : null;
}

export const ALWAYS_ALLOWED_SECTION_IDS = ['settings', 'help'];

export const LEGACY_SECTION_ID_ALIASES: Record<string, string[]> = {
  'social-media': ['marketing'],
  'commerce-v2': ['store'],
  'appointments-v2': ['scheduling'],
};

export function isSectionGranted(sectionId: string, menuPermissions: string[]): boolean {
  if (menuPermissions.includes(sectionId)) return true;
  const legacyIds = LEGACY_SECTION_ID_ALIASES[sectionId];
  return !!legacyIds && legacyIds.some((legacyId) => menuPermissions.includes(legacyId));
}

export function isRouteAllowed(pathname: string, entries: UrlIdEntry[], menuPermissions: string[]): boolean {
  const id = resolveIdFromEntries(pathname, entries);
  if (id == null) return true; // unmappable route -> never block
  if (ALWAYS_ALLOWED_SECTION_IDS.includes(id)) return true; // personal/help always reachable
  return isSectionGranted(id, menuPermissions);
}

export function firstGrantedPathFrom(groups: Group[], menuPermissions: string[]): string {
  if (menuPermissions.includes('overview')) return '/home';
  if (menuPermissions.includes('inbox')) return '/home/inbox';
  for (const group of groups || []) {
    if (isSectionGranted(group.id, menuPermissions)) {
      const first = group.secondaryItems?.[0]?.url;
      if (first) return first;
    }
  }
  return '/home';
}
